import "@sanity/ui/styles.css";
import { assist } from "@sanity/assist";
import { FormFieldSet, ObjectInputMember, defineArrayMember, defineDocumentFieldAction, defineField, definePlugin, defineType, getPublishedId, set, unset, useDocumentPreviewStore, useDocumentStore } from "sanity";
import { unsplashImageAsset } from "sanity-plugin-asset-source-unsplash";
import { structureTool } from "sanity/structure";
import { colorInput } from "@sanity/color-input";
import { CogIcon } from "@sanity/icons/Cog";
import { DocumentTextIcon } from "@sanity/icons/DocumentText";
import { UserIcon } from "@sanity/icons/User";
import { format, parseISO } from "date-fns";
import { defineDocuments, defineLocations, presentationTool } from "sanity/presentation";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { apiVersion, workspaces } from "@repo/env";
import { ControlsIcon } from "@sanity/icons/Controls";
import { EditIcon } from "@sanity/icons/Edit";
import { RevertIcon } from "@sanity/icons/Revert";
import { Card, Stack } from "@sanity/ui";
import { Code } from "@sanity/ui/code";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EMPTY, switchMap } from "rxjs";
const brandType = defineType(defineType({
	type: "document",
	name: "brand",
	fields: [
		defineField({
			type: "string",
			name: "name",
			title: "Name"
		}),
		defineField({
			type: "slug",
			name: "slug",
			title: "Slug",
			options: { source: "name" }
		}),
		defineField({
			type: "image",
			name: "logo",
			title: "Logo",
			options: {
				hotspot: !0,
				aiAssist: {
					imageDescriptionField: "alt",
					imageInstructionField: "imagePrompt"
				}
			},
			fields: [defineField({
				name: "alt",
				type: "string",
				title: "Alt text"
			}), defineField({
				type: "text",
				name: "imagePrompt",
				title: "Image prompt",
				rows: 2
			})]
		})
	]
})), crossDatasetReferencesPlugin = definePlugin({
	name: "@repo/sanity-schema/cross-dataset-references",
	schema: { types: [brandType] },
	plugins: [
		assist(),
		unsplashImageAsset(),
		structureTool()
	]
}), homeLocation = {
	title: "Home",
	href: "/"
}, authorType = defineType({
	name: "author",
	title: "Author",
	icon: UserIcon,
	type: "document",
	fields: [defineField({
		name: "name",
		title: "Name",
		type: "string",
		validation: (rule) => rule.required()
	}), defineField({
		name: "picture",
		title: "Picture",
		type: "image",
		fields: [{
			name: "alt",
			type: "string",
			title: "Alternative text",
			description: "Important for SEO and accessiblity.",
			validation: (rule) => rule.custom((alt, context) => (context.document?.picture)?.asset?._ref && !alt ? "Required" : !0)
		}],
		options: {
			hotspot: !0,
			aiAssist: { imageDescriptionField: "alt" }
		},
		validation: (rule) => rule.required()
	})]
}), postType = defineType({
	name: "post",
	title: "Post",
	icon: DocumentTextIcon,
	type: "document",
	fields: [
		defineField({
			name: "title",
			title: "Title",
			type: "string",
			validation: (rule) => rule.required()
		}),
		defineField({
			name: "slug",
			title: "Slug",
			type: "slug",
			description: "A slug is required for the post to show up in the preview",
			options: {
				source: "title",
				maxLength: 96,
				isUnique: (value, context) => context.defaultIsUnique(value, context)
			},
			validation: (rule) => rule.required()
		}),
		defineField({
			name: "content",
			title: "Content",
			type: "array",
			of: [{ type: "block" }]
		}),
		defineField({
			name: "excerpt",
			title: "Excerpt",
			type: "text"
		}),
		defineField({
			name: "coverImage",
			title: "Cover Image",
			type: "image",
			options: {
				hotspot: !0,
				aiAssist: { imageDescriptionField: "alt" }
			},
			fields: [{
				name: "alt",
				type: "string",
				title: "Alternative text",
				description: "Important for SEO and accessiblity.",
				validation: (rule) => rule.custom((alt, context) => (context.document?.coverImage)?.asset?._ref && !alt ? "Required" : !0)
			}],
			validation: (rule) => rule.required()
		}),
		defineField({
			name: "date",
			title: "Date",
			type: "datetime",
			initialValue: () => (/* @__PURE__ */ new Date()).toISOString()
		}),
		defineField({
			name: "author",
			title: "Author",
			type: "reference",
			to: [{ type: authorType.name }]
		})
	],
	preview: {
		select: {
			title: "title",
			author: "author.name",
			date: "date",
			media: "coverImage"
		},
		prepare({ title, media, author, date }) {
			return {
				title,
				media,
				subtitle: [author && `by ${author}`, date && `on ${format(parseISO(date), "LLL d, yyyy")}`].filter(Boolean).join(" ")
			};
		}
	}
}), settingsType = defineType({
	name: "settings",
	title: "Settings",
	type: "document",
	icon: CogIcon,
	fields: [
		defineField({
			name: "title",
			description: "This field is the title of your blog.",
			title: "Title",
			type: "string",
			initialValue: "Blog.",
			validation: (rule) => rule.required()
		}),
		defineField({
			name: "description",
			description: "Used both for the <meta> description tag for SEO, and the blog subheader.",
			title: "Description",
			type: "array",
			initialValue: [{
				_key: "9f1a629887fd",
				_type: "block",
				children: [
					{
						_key: "4a58edd077880",
						_type: "span",
						marks: [],
						text: "A statically generated blog example using "
					},
					{
						_key: "4a58edd077881",
						_type: "span",
						marks: ["ec5b66c9b1e0"],
						text: "Next.js"
					},
					{
						_key: "4a58edd077882",
						_type: "span",
						marks: [],
						text: " and "
					},
					{
						_key: "4a58edd077883",
						_type: "span",
						marks: ["1f8991913ea8"],
						text: "Sanity"
					},
					{
						_key: "4a58edd077884",
						_type: "span",
						marks: [],
						text: "."
					}
				],
				markDefs: [{
					_key: "ec5b66c9b1e0",
					_type: "link",
					href: "https://nextjs.org/"
				}, {
					_key: "1f8991913ea8",
					_type: "link",
					href: "https://sanity.io/"
				}],
				style: "normal"
			}],
			of: [defineArrayMember({
				type: "block",
				options: {},
				styles: [],
				lists: [],
				marks: {
					decorators: [],
					annotations: [defineField({
						type: "object",
						name: "link",
						fields: [{
							type: "string",
							name: "href",
							title: "URL",
							validation: (rule) => rule.required()
						}]
					})]
				}
			})]
		}),
		defineField({
			type: "object",
			name: "theme",
			fields: [{
				type: "color",
				name: "background",
				title: "Background Color"
			}, {
				type: "color",
				name: "text",
				title: "Text Color"
			}]
		}),
		defineField({
			name: "footer",
			description: "This is a block of text that will be displayed at the bottom of the page.",
			title: "Footer Info",
			type: "array",
			of: [defineArrayMember({
				type: "block",
				marks: { annotations: [{
					name: "link",
					type: "object",
					title: "Link",
					fields: [{
						name: "href",
						type: "url",
						title: "Url"
					}]
				}] }
			})]
		}),
		defineField({
			name: "ogImage",
			title: "Open Graph Image",
			type: "image",
			description: "Displayed on social cards and search engine results.",
			options: {
				hotspot: !0,
				aiAssist: { imageDescriptionField: "alt" }
			},
			fields: [defineField({
				name: "alt",
				description: "Important for accessibility and SEO.",
				title: "Alternative text",
				type: "string",
				validation: (rule) => rule.custom((alt, context) => (context.document?.ogImage)?.asset?._ref && !alt ? "Required" : !0)
			}), defineField({
				name: "metadataBase",
				type: "url",
				description: /* @__PURE__ */ jsx("a", {
					href: "https://nextjs.org/docs/app/api-reference/functions/generate-metadata#metadatabase",
					rel: "noreferrer noopener",
					children: "More information"
				})
			})]
		})
	],
	preview: { prepare() {
		return { title: "Settings" };
	} }
}), pageStructure = (typeDefArray) => (S) => {
	let singletonItems = typeDefArray.map((typeDef) => S.listItem().title(typeDef.title).icon(typeDef.icon).child(S.editor().id(typeDef.name).schemaType(typeDef.name).documentId(typeDef.name))), defaultListItems = S.documentTypeListItems().filter((listItem) => !typeDefArray.find((singleton) => singleton.name === listItem.getId()));
	return S.list().title("Content").items([
		...singletonItems,
		S.divider(),
		...defaultListItems
	]);
}, singletonPlugin = definePlugin((types) => ({
	name: "singletonPlugin",
	document: {
		newDocumentOptions: (prev, { creationContext }) => creationContext.type === "global" ? prev.filter((templateItem) => !types.includes(templateItem.templateId)) : prev,
		actions: (prev, { schemaType }) => types.includes(schemaType) ? prev.filter(({ action }) => action !== "duplicate") : prev
	}
})), assistWithPresets = () => assist({ __presets: { [postType.name]: { fields: [{
	/**
	* Creates Portable Text `content` blocks from the `title` field
	*/
	path: "content",
	instructions: [{
		_key: "preset-instruction-1",
		title: "Generate sample content",
		icon: "block-content",
		prompt: [
			{
				_key: "86e70087d4d5",
				markDefs: [],
				children: [
					{
						_type: "span",
						marks: [],
						text: "Given the draft title ",
						_key: "6b5d5d6a63cf0"
					},
					{
						path: "title",
						_type: "sanity.assist.instruction.fieldRef",
						_key: "0132742d463b"
					},
					{
						_type: "span",
						marks: [],
						text: " of a blog post, generate a comprehensive and engaging sample content that spans the length of one to two A4 pages. The content should be structured, informative, and tailored to the subject matter implied by the title, whether it be travel, software engineering, fashion, politics, or any other theme. The text will be displayed below the ",
						_key: "a02c9ab4eb2d"
					},
					{
						_type: "sanity.assist.instruction.fieldRef",
						_key: "f208ef240062",
						path: "title"
					},
					{
						text: " and doesn't need to repeat it in the text. The generated text should include the following elements:",
						_key: "8ecfa74a8487",
						_type: "span",
						marks: []
					}
				],
				_type: "block",
				style: "normal"
			},
			{
				style: "normal",
				_key: "e4dded41ea89",
				markDefs: [],
				children: [{
					_type: "span",
					marks: [],
					text: "1. Introduction: A brief paragraph that captures the essence of the blog post, hooks the reader with intriguing insights, and outlines the purpose of the post.",
					_key: "cc5ef44a2fb5"
				}],
				_type: "block"
			},
			{
				style: "normal",
				_key: "585e8de2fe35",
				markDefs: [],
				children: [{
					_type: "span",
					marks: [],
					text: "2. Main Body:",
					_key: "fab36eb7c541"
				}],
				_type: "block"
			},
			{
				_type: "block",
				style: "normal",
				_key: "e96b89ef6357",
				markDefs: [],
				children: [{
					_type: "span",
					marks: [],
					text: "- For thematic consistency, divide the body into several sections with subheadings that explore different facets of the topic.",
					_key: "b685a310a0ff"
				}]
			},
			{
				children: [{
					marks: [],
					text: "- Include engaging and informative content such as personal anecdotes (for travel or fashion blogs), technical explanations or tutorials (for software engineering blogs), satirical or humorous observations (for shitposting), or well-argued positions (for political blogs).",
					_key: "c7468d106c91",
					_type: "span"
				}],
				_type: "block",
				style: "normal",
				_key: "ce4acdb00da9",
				markDefs: []
			},
			{
				_type: "block",
				style: "normal",
				_key: "fb4572e65833",
				markDefs: [],
				children: [{
					_type: "span",
					marks: [],
					text: "- ",
					_key: "5358f261dce4"
				}, {
					_type: "span",
					marks: [],
					text: " observations (for shitposting), or well-argued positions (for political blogs).",
					_key: "50792c6d0f77"
				}]
			},
			{
				children: [{
					marks: [],
					text: "Where applicable, incorporate bullet points or numbered lists to break down complex information, steps in a process, or key highlights.",
					_key: "3b891d8c1dde0",
					_type: "span"
				}],
				_type: "block",
				style: "normal",
				_key: "9364b67074ce",
				markDefs: []
			},
			{
				_key: "a6ba7579cd66",
				markDefs: [],
				children: [{
					_type: "span",
					marks: [],
					text: "3. Conclusion: Summarize the main points discussed in the post, offer final thoughts or calls to action, and invite readers to engage with the content through comments or social media sharing.",
					_key: "1280f11d499d"
				}],
				_type: "block",
				style: "normal"
			},
			{
				style: "normal",
				_key: "719a79eb4c1c",
				markDefs: [],
				children: [{
					marks: [],
					text: "4. Engagement Prompts: Conclude with questions or prompts that encourage readers to share their experiences, opinions, or questions related to the blog post's topic, but keep in mind there is no Comments field below the blog post.",
					_key: "f1512086bab6",
					_type: "span"
				}],
				_type: "block"
			},
			{
				_type: "block",
				style: "normal",
				_key: "4a1c586fd44a",
				markDefs: [],
				children: [{
					marks: [],
					text: "Ensure the generated content maintains a balance between being informative and entertaining, to capture the interest of a wide audience. The sample content should serve as a solid foundation that can be further customized or expanded upon by the blog author to finalize the post.",
					_key: "697bbd03cb110",
					_type: "span"
				}]
			},
			{
				children: [{
					marks: [],
					text: "Don't prefix each section with \"Introduction\", \"Main Body\", \"Conclusion\" or \"Engagement Prompts\"",
					_key: "d20bb9a03b0d",
					_type: "span"
				}],
				_type: "block",
				style: "normal",
				_key: "b072b3c62c3c",
				markDefs: []
			}
		]
	}]
}, {
	/**
	* Summarize content into the `excerpt` field
	*/
	path: "excerpt",
	instructions: [{
		_key: "preset-instruction-2",
		title: "Summarize content",
		icon: "blockquote",
		prompt: [{
			markDefs: [],
			children: [
				{
					_key: "650a0dcc327d",
					_type: "span",
					marks: [],
					text: "Create a short excerpt based on "
				},
				{
					path: "content",
					_type: "sanity.assist.instruction.fieldRef",
					_key: "c62d14c73496"
				},
				{
					_key: "38e043efa606",
					_type: "span",
					marks: [],
					text: " that doesn't repeat what's already in the "
				},
				{
					path: "title",
					_type: "sanity.assist.instruction.fieldRef",
					_key: "445e62dda246"
				},
				{
					_key: "98cce773915e",
					_type: "span",
					marks: [],
					text: " . Consider the UI has limited horizontal space and try to avoid too many line breaks and make it as short, terse and brief as possible. At best a single sentence, at most two sentences."
				}
			],
			_type: "block",
			style: "normal",
			_key: "392c618784b0"
		}]
	}]
}] } } }), liveDemoPlugin = definePlugin((config) => ({
	name: "@repo/sanity-schema/live-demo",
	schema: { types: [
		settingsType,
		postType,
		authorType
	] },
	plugins: [
		colorInput(),
		presentationTool({
			resolve: {
				mainDocuments: defineDocuments([{
					route: "/posts/:slug",
					filter: "_type == \"post\" && slug.current == $slug"
				}]),
				locations: {
					settings: defineLocations({
						locations: [homeLocation],
						message: "This document is used on all pages",
						tone: "caution"
					}),
					post: defineLocations({
						select: {
							title: "title",
							slug: "slug.current"
						},
						resolve: (doc) => ({ locations: doc?.slug ? [{
							title: doc?.title || "Untitled",
							href: `/posts/${doc.slug}`
						}, homeLocation] : [homeLocation] })
					})
				}
			},
			...config
		}),
		structureTool({ structure: pageStructure([settingsType]) }),
		singletonPlugin([settingsType.name]),
		unsplashImageAsset(),
		assistWithPresets()
	]
}));
function useReferenceEditState(documentId) {
	let documentStore = useDocumentStore(), previewStore = useDocumentPreviewStore(), [editState, setEditState] = useState();
	return useEffect(() => {
		if (startTransition(() => setEditState(void 0)), !documentId) return;
		let sub = previewStore.observeDocumentTypeFromId(documentId).pipe(switchMap((type) => type ? documentStore.pair.editState(getPublishedId(documentId), type) : EMPTY)).subscribe({ next: setEditState });
		return () => sub.unsubscribe();
	}, [
		documentId,
		documentStore,
		previewStore
	]), editState;
}
function isFieldMember(member) {
	return member.kind === "field";
}
function findFieldMember(node, name) {
	return node.members.filter(isFieldMember).find((m) => m.name === name);
}
function PageSectionInput(props) {
	let { level, onChange, path, renderField: renderFieldProp, renderInput, renderItem, renderPreview, value } = props, symbolFieldMember = findFieldMember(props, "symbol"), refEditState = useReferenceEditState(value?.symbol?._ref), symbolValue = refEditState?.draft || refEditState?.published || void 0, symbolValueRef = useRef(symbolValue), symbolAction = useMemo(() => defineDocumentFieldAction({
		name: "unlock",
		useAction(actionProps) {
			let relativeFieldPath = actionProps.path.slice(path.length), fieldSegment = relativeFieldPath[0], isOverridden = typeof fieldSegment == "string" && value?.[fieldSegment] !== void 0;
			return {
				type: "action",
				disabled: fieldSegment !== "string" || symbolValue?.[fieldSegment] === void 0,
				onAction() {
					typeof fieldSegment == "string" && onChange(isOverridden ? unset(relativeFieldPath) : set(symbolValue?.[fieldSegment] || "", relativeFieldPath));
				},
				icon: isOverridden ? RevertIcon : EditIcon,
				title: isOverridden ? "Revert to symbol value" : "Edit to override",
				renderAsButton: !0
			};
		}
	}), [
		onChange,
		path,
		symbolValue,
		value
	]);
	useEffect(() => {
		symbolValueRef.current = symbolValue;
	}, [symbolValue]);
	let renderField = useCallback((fieldProps) => renderFieldProp({
		...fieldProps,
		actions: (fieldProps.actions || []).concat(symbolValue ? [symbolAction] : [])
	}), [
		renderFieldProp,
		symbolAction,
		symbolValue
	]);
	return /* @__PURE__ */ jsxs(Stack, {
		gap: 5,
		children: [
			symbolFieldMember && /* @__PURE__ */ jsx(Card, {
				padding: 4,
				style: { margin: "-20px -20px -20px" },
				children: /* @__PURE__ */ jsx(ObjectInputMember, {
					member: symbolFieldMember,
					renderField: renderFieldProp,
					renderInput,
					renderItem,
					renderPreview
				})
			}),
			/* @__PURE__ */ jsx(FormFieldSet, {
				inputId: "",
				level: level + 1,
				path,
				title: "Overrides",
				children: props.members.map((mem) => isFieldMember(mem) && mem.name === "symbol" ? null : mem.kind === "field" ? /* @__PURE__ */ jsx(ObjectInputMember, {
					member: {
						...mem,
						field: {
							...mem.field,
							readOnly: !!symbolValue && mem.field.value === void 0,
							value: mem.field.value ?? symbolValue?.[mem.name]
						}
					},
					renderField,
					renderInput,
					renderItem,
					renderPreview
				}, mem.key) : /* @__PURE__ */ jsx(ObjectInputMember, {
					member: mem,
					renderField,
					renderInput,
					renderItem,
					renderPreview
				}, mem.key))
			}),
			/* @__PURE__ */ jsx(Code, {
				hidden: !0,
				language: "json",
				size: 0,
				children: JSON.stringify(symbolValue || null, null, 2)
			})
		]
	});
}
const { dataset: dataset$1, workspace: workspace$1, tool: tool$1 } = workspaces["cross-dataset-references"], productType = defineType({
	type: "document",
	name: "product",
	fields: [
		defineField({
			type: "string",
			name: "title",
			title: "Title"
		}),
		defineField({
			type: "slug",
			name: "slug",
			title: "Slug",
			options: { source: "title" }
		}),
		defineField({
			type: "array",
			name: "media",
			title: /* @__PURE__ */ jsx(Fragment, { children: "Media" }),
			of: [{
				type: "image",
				fields: [defineField({
					name: "alt",
					type: "string",
					title: "Alt text"
				})]
			}],
			options: { layout: "grid" }
		}),
		defineField({
			type: "array",
			name: "description",
			title: "Description",
			of: [{ type: "block" }]
		}),
		defineField({
			title: "Brand",
			name: "brandReference",
			type: "crossDatasetReference",
			dataset: dataset$1,
			studioUrl: ({ type, id }) => new URL(`/${workspace$1}/${tool$1}/intent/edit/id=${id};type=${type}/`, location.href).toString(),
			to: [{
				type: "brand",
				preview: { select: {
					title: "name",
					media: "logo"
				} }
			}]
		}),
		defineField({
			type: "object",
			name: "details",
			title: "Details",
			fields: [
				defineField({
					type: "string",
					name: "materials",
					title: "Materials"
				}),
				defineField({
					type: "array",
					name: "collectionNotes",
					title: "Collection notes",
					of: [{ type: "block" }]
				}),
				defineField({
					type: "array",
					name: "performance",
					title: "Performance",
					of: [{ type: "block" }]
				}),
				defineField({
					type: "string",
					name: "ledLifespan",
					title: "LED lifespan"
				}),
				defineField({
					type: "array",
					name: "certifications",
					title: "Certifications",
					of: [{ type: "string" }]
				})
			]
		}),
		defineField({
			type: "array",
			name: "variants",
			title: "Variants",
			of: [defineArrayMember({
				type: "object",
				name: "variant",
				title: "Variant",
				fields: [
					defineField({
						type: "string",
						name: "title",
						title: "Title"
					}),
					defineField({
						type: "string",
						name: "price",
						title: "Price"
					}),
					defineField({
						type: "string",
						name: "sku",
						title: "SKU"
					})
				]
			})]
		})
	]
}), projectType = defineType({
	type: "document",
	name: "project",
	fields: [defineField({
		type: "string",
		name: "title",
		title: "Title"
	}), defineField({
		type: "slug",
		name: "slug",
		title: "Slug",
		options: { source: "title" }
	})]
}), siteSettingsType = defineType({
	type: "document",
	icon: ControlsIcon,
	name: "siteSettings",
	title: "Site Settings",
	fields: [
		{
			type: "string",
			name: "title",
			title: "Site Title"
		},
		{
			type: "string",
			name: "description",
			title: "Site Description"
		},
		{
			type: "reference",
			name: "frontPage",
			title: "Front page",
			to: [{ type: "page" }]
		},
		{
			type: "string",
			name: "copyrightText",
			title: "Copyright text"
		}
	],
	preview: { prepare() {
		return { title: "Site Settings" };
	} }
}), pageSectionType = defineType({
	type: "document",
	name: "page.section",
	fields: [
		defineField({
			type: "string",
			name: "headline",
			title: "Headline"
		}),
		defineField({
			type: "string",
			name: "tagline",
			title: "Tagline"
		}),
		defineField({
			type: "string",
			name: "subline",
			title: "Subline"
		})
	]
}), pageSectionArrayMember = defineArrayMember({
	type: "object",
	name: "section",
	fields: [defineField({
		type: "reference",
		name: "symbol",
		title: "Symbol",
		to: [{ type: "page.section" }]
	}), ...pageSectionType.fields],
	components: { input: PageSectionInput }
}), sectionStyleType = defineType({
	type: "object",
	name: "sectionStyle",
	title: "Section style",
	fields: [defineField({
		type: "string",
		name: "variant",
		title: "Variant",
		options: { list: [{
			title: "Default",
			value: "default"
		}, {
			title: "Inverted",
			value: "inverted"
		}] },
		initialValue: "default",
		validation: (rule) => rule.required()
	})]
}), featuredProductsSectionType = defineArrayMember({
	type: "object",
	name: "featuredProducts",
	title: "Featured Products",
	fields: [
		defineField({
			type: "string",
			name: "headline",
			title: "Headline"
		}),
		defineField({
			type: "string",
			name: "description",
			title: "Description"
		}),
		defineField({
			type: "array",
			name: "products",
			title: "Products",
			of: [{
				type: "reference",
				to: [{ type: "product" }]
			}]
		}),
		defineField({
			type: "sectionStyle",
			name: "style",
			title: "Style"
		})
	],
	preview: {
		select: { title: "headline" },
		prepare(data) {
			return {
				...data,
				subtitle: "Featured Products"
			};
		}
	}
}), heroSectionType = defineArrayMember({
	type: "object",
	name: "hero",
	title: "Hero",
	description: "The hero section of the page",
	fields: [
		defineField({
			type: "string",
			name: "headline",
			title: "Headline"
		}),
		defineField({
			type: "string",
			name: "tagline",
			title: "Tagline"
		}),
		defineField({
			type: "string",
			name: "subline",
			title: "Subline"
		}),
		defineField({
			type: "image",
			name: "image",
			title: "Image"
		}),
		defineField({
			type: "sectionStyle",
			name: "style",
			title: "Style"
		})
	],
	preview: {
		select: { title: "headline" },
		prepare(data) {
			return {
				...data,
				subtitle: "Hero"
			};
		}
	}
}), introSectionType = defineArrayMember({
	type: "object",
	name: "intro",
	title: "Intro",
	fields: [
		defineField({
			type: "string",
			name: "headline",
			title: "Headline"
		}),
		defineField({
			type: "text",
			name: "intro",
			title: "Intro"
		}),
		defineField({
			type: "sectionStyle",
			name: "style",
			title: "Style"
		}),
		defineField({
			type: "object",
			name: "rotations",
			title: "Rotations",
			fields: [defineField({
				type: "number",
				name: "pitch",
				title: "Pitch"
			}), defineField({
				type: "number",
				name: "yaw",
				title: "Yaw"
			})]
		})
	],
	preview: {
		select: { title: "headline" },
		prepare(data) {
			return {
				...data,
				subtitle: "Intro"
			};
		}
	}
}), featureHighlightSectionType = defineArrayMember({
	type: "object",
	name: "featureHighlight",
	title: "Feature Highlight",
	fields: [
		defineField({
			type: "string",
			name: "headline",
			title: "Headline"
		}),
		defineField({
			type: "text",
			name: "description",
			title: "Description"
		}),
		defineField({
			type: "image",
			name: "image",
			title: "Image"
		}),
		defineField({
			type: "array",
			name: "ctas",
			title: "CTAs",
			of: [{
				type: "object",
				name: "cta",
				title: "CTA",
				fields: [defineField({
					type: "string",
					name: "title",
					title: "Title"
				}), defineField({
					type: "string",
					name: "href",
					title: "Href"
				})]
			}]
		}),
		defineField({
			type: "reference",
			name: "product",
			title: "Product",
			to: [{ type: "product" }]
		}),
		defineField({
			type: "sectionStyle",
			name: "style",
			title: "Style"
		})
	],
	preview: {
		select: { title: "headline" },
		prepare(data) {
			return {
				...data,
				subtitle: "Feature"
			};
		}
	}
}), pageType = defineType({
	type: "document",
	name: "page",
	fields: [
		defineField({
			type: "string",
			name: "title",
			title: "Title"
		}),
		defineField({
			type: "slug",
			name: "slug",
			title: "Slug",
			options: { source: "title" },
			hidden: (ctx) => ["home", "drafts.home"].includes(ctx.document?._id)
		}),
		defineField({
			type: "array",
			name: "sections",
			title: "Sections",
			options: { insertMenu: { views: [{ name: "list" }, { name: "grid" }] } },
			of: [
				heroSectionType,
				introSectionType,
				featuredProductsSectionType,
				featureHighlightSectionType,
				pageSectionArrayMember
			]
		})
	]
}), dndTestItemChildL2 = defineArrayMember({
	type: "object",
	name: "dndTestItemChildL2",
	fields: [defineField({
		type: "string",
		name: "title",
		title: "Title"
	})]
}), dndTestItemChildL1 = defineArrayMember({
	type: "object",
	name: "dndTestItemChildL1",
	fields: [defineField({
		type: "string",
		name: "title",
		title: "Title"
	}), defineField({
		type: "array",
		name: "children",
		title: "Children",
		of: [dndTestItemChildL2]
	})]
}), dndTestItemRoot = defineArrayMember({
	type: "object",
	name: "dndTestItem",
	fields: [
		defineField({
			type: "string",
			name: "title",
			title: "Title"
		}),
		defineField({
			type: "array",
			name: "childrenStrings",
			title: "Children Strings",
			of: [{ type: "string" }]
		}),
		defineField({
			type: "array",
			name: "children",
			title: "Children",
			of: [dndTestItemChildL1]
		})
	]
}), dndTestPageType = defineType({
	type: "document",
	name: "dndTestPage",
	fields: [defineField({
		type: "string",
		name: "title",
		title: "Title"
	}), defineField({
		type: "array",
		name: "children",
		title: "Children",
		of: [dndTestItemRoot]
	})]
}), pageBuilderDemoPlugin = definePlugin((config) => ({
	name: "@repo/sanity-schema/page-builder-demo",
	schema: {
		types: [
			dndTestPageType,
			pageType,
			pageSectionType,
			productType,
			projectType,
			siteSettingsType,
			heroSectionType,
			introSectionType,
			featuredProductsSectionType,
			featureHighlightSectionType,
			sectionStyleType,
			pageSectionArrayMember
		],
		templates: [{
			id: "page-basic",
			title: "Basic page",
			schemaType: "page",
			parameters: [{
				name: "title",
				title: "Page Title",
				type: "string"
			}],
			value: (params) => ({
				title: params.title,
				slug: { current: "basic-slug" }
			})
		}]
	},
	plugins: [presentationTool({
		resolve: {
			mainDocuments: defineDocuments([
				{
					route: "/product/:slug",
					filter: "_type == \"product\" && slug.current == $slug"
				},
				{
					route: "/products",
					filter: "_type == \"page\" && slug.current == \"products\""
				},
				{
					route: "/",
					filter: "_type == \"page\" && (*[_id == \"siteSettings\"][0].frontPage._ref == _id)"
				},
				{
					route: "/dnd",
					filter: "_type == \"dndTestPage\""
				}
			]),
			locations: {
				page: defineLocations({
					select: {
						title: "title",
						slug: "slug.current"
					},
					resolve: (doc) => ({ locations: doc?.slug ? [{
						title: doc?.title || "Untitled",
						href: `/pages/${doc.slug}`
					}] : void 0 })
				}),
				product: defineLocations({
					select: {
						title: "title",
						slug: "slug"
					},
					resolve: (doc) => ({ locations: [{
						title: doc?.title || "Untitled",
						href: `/product/${doc?.slug?.current}`
					}, {
						title: "Products",
						href: "/products"
					}] })
				}),
				siteSettings: defineLocations({
					message: "This document is used on all pages",
					tone: "caution"
				})
			}
		},
		...config
	}), structureTool()]
})), performanceTestPlugin = definePlugin({
	name: "@repo/sanity-schema/performance-test",
	schema: { types: [
		{
			name: "Building",
			title: "Building",
			type: "document",
			fields: [
				{
					title: "Building Title",
					name: "title",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					name: "name",
					title: "Name",
					description: "Displayed in Campus App UI",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					name: "address",
					title: "Building Address",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					name: "city",
					title: "City",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Postal Code",
					name: "zip",
					type: "string"
				},
				{
					title: "Building Description",
					name: "buildingDescription",
					type: "string"
				},
				{
					title: "Building Status",
					name: "status",
					type: "string",
					validation: (Rule) => Rule.required(),
					options: {
						list: [{
							title: "Closed",
							value: "1a"
						}, {
							title: "Open",
							value: "2a"
						}],
						layout: "radio"
					}
				},
				{
					title: "Building Id",
					name: "buildingId",
					type: "string",
					readOnly: !0
				},
				{
					title: "Building Location",
					name: "location",
					type: "geopoint"
				},
				{
					title: "Building Timezone",
					name: "timezone",
					type: "string"
				},
				{
					title: "Floors",
					name: "floors",
					type: "array",
					of: [{
						type: "reference",
						to: [{ type: "Floor" }]
					}]
				},
				{
					title: "Promoted Building",
					name: "promotedBuilding",
					type: "boolean",
					initialValue: !1,
					readOnly: !1
				}
			]
		},
		{
			name: "Floor",
			title: "Floor",
			type: "document",
			fields: [
				{
					title: "Title",
					name: "title",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Name",
					name: "name",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Label",
					name: "label",
					type: "string"
				},
				{
					title: "Spaces",
					name: "spaces",
					type: "array",
					of: [{
						type: "reference",
						to: [{ type: "Space" }]
					}]
				}
			]
		},
		{
			name: "Space",
			title: "Space",
			type: "document",
			fields: [
				{
					title: "Title",
					name: "title",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Name",
					name: "name",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Space Code",
					name: "spaceCode",
					type: "string",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Description",
					name: "description",
					type: "string"
				},
				{
					title: "Contact Number",
					name: "contactNumber",
					type: "string"
				},
				{
					title: "Capacity",
					name: "capacity",
					type: "number",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Is Reservable",
					name: "isReservable",
					type: "boolean",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Type",
					name: "spaceType",
					type: "array",
					validation: (Rule) => Rule.required(),
					of: [{
						type: "reference",
						to: [{ type: "SpaceType" }]
					}]
				},
				{
					title: "Is Active",
					name: "isActive",
					type: "boolean",
					validation: (Rule) => Rule.required()
				},
				{
					title: "Video Device Name",
					name: "videoDevice",
					type: "string"
				},
				{
					title: "Display Device Name",
					name: "displayDevice",
					type: "string"
				},
				{
					title: "Audio Device Name",
					name: "audioDevice",
					type: "string"
				},
				{
					title: "Tags",
					name: "exchangeTags",
					type: "array",
					of: [{ type: "string" }]
				},
				{
					title: "Rating",
					name: "rating",
					type: "number"
				}
			]
		},
		{
			name: "SpaceType",
			title: "Space Type",
			type: "document",
			fields: [
				{
					title: "Title",
					name: "title",
					type: "string",
					validation: (Rule) => Rule.required().min(1).error("Space Type Title is required.")
				},
				{
					title: "Name",
					name: "name",
					type: "string",
					validation: (Rule) => Rule.required().min(1).error("Space Type Name is required.")
				},
				{
					title: "Value",
					name: "value",
					type: "string",
					initialValue: "Space"
				},
				{
					title: "System Id",
					name: "systemId",
					type: "string"
				}
			]
		}
	] },
	plugins: [structureTool()]
}), { dataset, workspace, tool } = workspaces["cross-dataset-references"], shoeType = defineType({
	type: "document",
	name: "shoe",
	fields: [
		defineField({
			type: "string",
			name: "title",
			title: "Title",
			validation: (rule) => rule.required()
		}),
		defineField({
			type: "slug",
			name: "slug",
			title: "Slug",
			options: {
				source: "title",
				isUnique: async (slug, context) => {
					let { document, getClient } = context;
					return !document?._type || await getClient({ apiVersion }).fetch("count(*[_type == $type && slug.current == $slug])", {
						type: document._type,
						slug
					}, { perspective: "drafts" }) < 2;
				}
			},
			validation: (rule) => rule.required()
		}),
		defineField({
			type: "array",
			name: "media",
			title: "Media",
			of: [defineArrayMember({
				type: "image",
				options: {
					hotspot: !0,
					aiAssist: {
						imageDescriptionField: "alt",
						imageInstructionField: "imagePrompt"
					}
				},
				fields: [defineField({
					name: "alt",
					type: "string",
					title: "Alt text"
				}), defineField({
					type: "text",
					name: "imagePrompt",
					title: "Image prompt",
					rows: 2
				})]
			})],
			options: { layout: "grid" }
		}),
		defineField({
			type: "array",
			name: "description",
			title: "Description",
			of: [{ type: "block" }]
		}),
		defineField({
			type: "number",
			name: "price",
			title: "Price"
		}),
		defineField({
			title: "Brand",
			name: "brandReference",
			type: "crossDatasetReference",
			dataset,
			studioUrl: ({ type, id }) => new URL(`/${workspace}/${tool}/intent/edit/id=${id};type=${type}/`, location.href).toString(),
			to: [{
				type: "brand",
				preview: { select: {
					title: "name",
					media: "logo"
				} }
			}]
		})
	],
	preview: { select: {
		title: "title",
		media: "media.0.asset"
	} }
}), shoesPlugin = definePlugin((config) => ({
	name: "@repo/sanity-schema/shoes",
	schema: { types: [shoeType] },
	plugins: [
		assist(),
		unsplashImageAsset(),
		presentationTool({
			resolve: {
				mainDocuments: defineDocuments([{
					route: "/shoes/:slug",
					filter: "_type == \"shoe\" && slug.current == $slug"
				}]),
				locations: { shoe: defineLocations({
					select: {
						title: "title",
						slug: "slug.current"
					},
					resolve: (doc) => ({ locations: [{
						title: doc?.title || "Untitled",
						href: `/shoes/${doc?.slug}`
					}, {
						title: "Shoes",
						href: "/shoes"
					}] })
				}) },
				...config.resolve
			},
			...config
		}),
		structureTool()
	]
}));
export { crossDatasetReferencesPlugin, liveDemoPlugin, pageBuilderDemoPlugin, performanceTestPlugin, shoesPlugin };

//# sourceMappingURL=index.js.map